/** QuickHouse — Plans de coupe 3D : masque tout ce qui est au-delà du plan, sur 3 axes.
 *  Utilise le clipping global du renderer (aucun matériau à modifier). */
import * as THREE from 'three';
import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { useProjectStore } from '../store/useProjectStore.js';
import { wallsBounds } from '../../shared/geometry.js';
import { floorElevations } from '../../shared/plan3d.js';

// Axe modèle → direction monde : x plan → x, y plan → z, hauteur → y
const AXIS_DIRS = { x: [1, 0, 0], y: [0, 0, 1], z: [0, 1, 0] };

export default function ClipPlanes() {
  const gl = useThree((s) => s.gl);
  const clip = useProjectStore((s) => s.clip);

  useEffect(() => {
    const planes = [];
    for (const [axis, dir] of Object.entries(AXIS_DIRS)) {
      const c = clip[axis];
      if (!c?.on || c.value == null) continue;
      const v = c.value / 100; // cm → m
      // flip=false : masque au-delà de v (garde n·p + d ≥ 0 avec n = −axe, d = v)
      const n = new THREE.Vector3(...dir).multiplyScalar(c.flip ? 1 : -1);
      planes.push(new THREE.Plane(n, c.flip ? -v : v));
    }
    gl.clippingPlanes = planes;
    return () => { gl.clippingPlanes = []; };
  }, [gl, clip]);

  return null;
}

/** Bornes du modèle (cm) pour les curseurs de coupe : { x: [min,max], y: [min,max], z: [0,max] } */
export function modelBounds(project) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const eat = (b) => {
    if (!b) return;
    minX = Math.min(minX, b.minX);
    minY = Math.min(minY, b.minY);
    maxX = Math.max(maxX, b.maxX);
    maxY = Math.max(maxY, b.maxY);
  };
  for (const floor of project.floors) {
    eat(wallsBounds(floor.walls));
    for (const room of floor.rooms) {
      for (const block of room.blocks ?? []) {
        if (block.type === 'rect') {
          eat({ minX: block.x, minY: block.y, maxX: block.x + block.w, maxY: block.y + block.h });
        } else {
          for (const p of block.points) eat({ minX: p.x, minY: p.y, maxX: p.x, maxY: p.y });
        }
      }
    }
  }
  if (minX === Infinity) { minX = 0; minY = 0; maxX = 1000; maxY = 1000; }
  const elevs = floorElevations(project.floors, project.settings.slabThickness);
  const last = elevs[elevs.length - 1];
  const lastFloor = project.floors.find((f) => f.id === last?.floorId);
  const zMax = (last?.elevation ?? 0) + (lastFloor?.height ?? 250) + 300;
  return {
    x: [Math.floor(minX - 50), Math.ceil(maxX + 50)],
    y: [Math.floor(minY - 50), Math.ceil(maxY + 50)],
    z: [0, Math.ceil(zMax)],
  };
}
